import React, {
  memo,
  // eslint-disable-next-line no-restricted-imports
  Suspense as ReactSuspense,
  useCallback,
  useEffect,
} from 'react'
import { IAPIError } from 'types'
import { useIsPrerendering } from 'hooks/utility'
import ErrorBoundary from './ErrorBoundary'

interface SuspenseProps {
  fallback?: any
  ErrorFallbackComponent?: React.ElementType<{ error: IAPIError }> | false
  showErrorReportDialog?: boolean
  noSsr?: boolean
  children?: any
}

export const SuspenseContext = React.createContext({
  lockClientOnlyRender: () => {},
})

/**
 * Our enhanced version of the basic react Suspense boundary.
 */
function Suspense({ children, noSsr = false, fallback = null, showErrorReportDialog, ErrorFallbackComponent }: SuspenseProps) {
  const isSsr = useIsPrerendering({ useTransition: false })
  const [isClientOnlyRender, setClientOnlyRenderCount] = React.useState(false)
  const [clientRendered, setClientRendered] = React.useState(false)

  const lockClientOnlyRender = useCallback(() => {
    if (!isClientOnlyRender) {
      setClientOnlyRenderCount(true)
    }
  }, [isClientOnlyRender])

  useEffect(() => {
    setClientRendered(true)
  }, [])

  if (isSsr) {
    if (noSsr || isClientOnlyRender) {
      return fallback
    }
  }

  if ((noSsr || isClientOnlyRender) && !clientRendered) {
    return fallback
  }

  // NOTE: make sure to NOT pass undefined as fallback to React's Suspense or it will error out!!!
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallbackComponent === false ? null : ErrorFallbackComponent || fallback}
      showErrorReportDialog={showErrorReportDialog}
    >
      <SuspenseContext.Provider value={{ lockClientOnlyRender }}>
        <ReactSuspense fallback={fallback}>
          {children}
        </ReactSuspense>
      </SuspenseContext.Provider>
    </ErrorBoundary>
  )
}

export default memo(Suspense)
